import { jsPDF } from "jspdf";
import {
  TEXT_X_MM, TEXT_WIDTH_MM,
  drawHeader, drawFooter, renderCategoryPage,
  type InvestigationCategory,
} from "./investigationLayout";

// 2026-11-01, Batch 7 Wave 2 dedupe — the helpers that used to be inlined in
// this renderer (drawPatientIdentityRows, drawMixedLine, classifyInvestigation,
// renderDetailsTable, renderCategoryPage) and the InvestigationCategory type
// were extracted verbatim to ./layoutShared.ts and are re-exported through
// this renderer's layout sibling (investigationLayout). fmtDatePdf /
// computeAgeYm / formatAgeLabel moved with drawPatientIdentityRows (their only
// uses here). The former public exports are preserved unchanged for importers:
export { classifyInvestigation, renderCategoryPage, type InvestigationCategory } from "./investigationLayout";

export interface InvestigationPdfData {
  patientName: string;
  clinicUhid: string;
  patientDob: string | null;
  patientGender: string | null;
  checkInAt: string;
  diagnosis: string | null;
  selectedTests: { id: string; name: string }[];
  notes: string | null;
  doctorName: string | null;
  doctorRegNumber: string | null;
}

/**
 * Per-investigation metadata for Rad/EP tests. Keyed by investigation ID.
 * Only tests with at least one non-null field are stored; absent keys
 * mean "no data" — the row is omitted from the print table.
 */
export interface InvestigationDetailEntry {
  regionAndSide: string | null;
  clinicalNotes: string | null;
}
export type InvestigationDetailsMap = Record<string, InvestigationDetailEntry>;

export interface CombinedInvestigationPdfData {
  patientName: string;
  clinicUhid: string;
  patientDob: string | null;
  patientGender: string | null;
  checkInAt: string;
  diagnosis: string | null;
  categories: {
    category: InvestigationCategory;
    tests: { id: string; name: string }[];
    notes: string | null;
  }[];
  // Per-investigation Region & Side / Clinical Notes for Rad/EP tests.
  investigationDetails: Record<string, { regionAndSide: string; clinicalNotes: string }>;
  doctorName: string | null;
  doctorRegNumber: string | null;
}

export async function createInvestigationSheetPdf(
  data: InvestigationPdfData,
  category: InvestigationCategory,
): Promise<jsPDF> {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);

  await drawHeader(doc);
  await drawFooter(doc);

  await renderCategoryPage(doc, { ...data, category, tests: data.selectedTests });
  return doc;
}

/**
 * Combined investigation sheet: Lab on page 1, Radiology on page 2,
 * Electrophysiology on page 3 — one page per category that has selected
 * tests, separated by page breaks so each prints on its own sheet.
 */
export async function createCombinedInvestigationPdf(
  data: CombinedInvestigationPdfData,
): Promise<jsPDF> {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);

  const pages = data.categories.filter((c) => c.tests.length > 0);

  for (let i = 0; i < pages.length; i++) {
    if (i > 0) doc.addPage();
    await drawHeader(doc);
    await drawFooter(doc);
    await renderCategoryPage(doc, {
      patientName: data.patientName,
      clinicUhid: data.clinicUhid,
      patientDob: data.patientDob,
      patientGender: data.patientGender,
      checkInAt: data.checkInAt,
      diagnosis: data.diagnosis,
      category: pages[i].category,
      tests: pages[i].tests,
      notes: pages[i].notes,
      doctorName: data.doctorName,
      doctorRegNumber: data.doctorRegNumber,
      investigationDetails: data.investigationDetails,
    });
  }

  return doc;
}
